/**
 * Utility functions shared across the application
 */

/**
 * Format a number as Mexican currency
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatPrice = (amount) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(amount);
};

/**
 * Format a date string to localized format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format a date string to short format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string (e.g., "15 Jun 2024")
 */
export const formatDateShort = (dateString) => {
  return new Date(dateString).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Validate a phone number (minimum 10 digits)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} Whether the phone is valid
 */
export const validatePhoneNumber = (phone) => {
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  return cleanPhone.length >= 10;
};

/**
 * Format a phone number for display
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }
  return phone;
};

/**
 * Generate a unique ID for React keys
 * @returns {string} Unique identifier
 */
export const generateUniqueId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Truncate text to a maximum length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format shipping address for display
 * @param {object} address - Shipping address object
 * @returns {string} Formatted address string
 */
export const formatShippingAddress = (address) => {
  if (!address) return '';
  
  let formatted = `${address.street} ${address.exterior_number}`;
  if (address.interior_number) {
    formatted += `, Int. ${address.interior_number}`;
  }
  formatted += `, ${address.neighborhood}, ${address.municipality}`;
  formatted += `, ${address.state}, C.P. ${address.postal_code}`;
  
  return formatted;
};
